<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { call, identity, ApiError, type Schemas } from '../api/http'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { formatDate, LOCALES, setLocale, type Locale } from '../i18n'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiBadge from '../components/UiBadge.vue'
import UiField from '../components/UiField.vue'
import ErrorBanner from '../components/ErrorBanner.vue'

const { t } = useI18n()
const session = useSession()

// Profile
const profile = useForm()
const profileSaved = ref(false)
const pf = reactive({ display_name: session.user?.display_name ?? '', locale: (session.user?.locale ?? 'en') as Locale })
async function saveProfile() {
  profileSaved.value = await profile.submit(async () => {
    session.user = await call(identity.PATCH('/me', { body: { ...pf } }))
    setLocale(pf.locale)
  })
}

// Password
const password = useForm()
const pwSaved = ref(false)
const pw = reactive({ current_password: '', new_password: '', confirm: '' })
async function savePassword() {
  pwSaved.value = await password.submit(async () => {
    if (pw.new_password !== pw.confirm) {
      throw new ApiError({ status: 0, code: 'validation_failed', errors: [{ field: 'confirm', message: t('settings.passwordMismatch') }] })
    }
    await call(identity.PUT('/me/password', { body: { current_password: pw.current_password, new_password: pw.new_password } }))
    Object.assign(pw, { current_password: '', new_password: '', confirm: '' })
    await loadSessions()
  })
}

// Sessions
const sessionsForm = useForm()
const sessions = ref<Schemas['SessionInfo'][]>([])
const loadSessions = () => sessionsForm.submit(async () => (sessions.value = await call(identity.GET('/me/sessions'))))
const revoke = (s: Schemas['SessionInfo']) =>
  sessionsForm.submit(async () => {
    await call(identity.DELETE('/me/sessions/{session_id}', { params: { path: { session_id: s.id } } }))
    sessions.value = sessions.value.filter((x) => x.id !== s.id)
  })
onMounted(loadSessions)
</script>

<template>
  <div class="page">
    <header class="page-header">
      <h1>{{ $t('settings.title') }}</h1>
    </header>

    <UiCard :title="$t('settings.profileTitle')">
      <form class="stack" @submit.prevent="saveProfile">
        <ErrorBanner :error="profile.error.value" />
        <div class="grid-2">
          <UiField :label="$t('fields.displayName')" :error="profile.fieldErr('display_name')">
            <input v-model.trim="pf.display_name" name="display_name" required maxlength="100" :aria-invalid="!!profile.fieldErr('display_name')" />
          </UiField>
          <UiField :label="$t('fields.locale')" :error="profile.fieldErr('locale')">
            <select v-model="pf.locale" name="locale">
              <option v-for="l in LOCALES" :key="l" :value="l">{{ $t(`locales.${l}`) }}</option>
            </select>
          </UiField>
        </div>
        <UiField :label="$t('fields.email')">
          <input :value="session.user?.email" disabled />
        </UiField>
        <div class="row">
          <UiButton type="submit" :loading="profile.busy.value">{{ $t('common.save') }}</UiButton>
          <span v-if="profileSaved" class="small ok" role="status">{{ $t('common.saved') }}</span>
        </div>
      </form>
    </UiCard>

    <UiCard :title="$t('settings.passwordTitle')" :subtitle="$t('settings.passwordSubtitle')">
      <form class="stack" @submit.prevent="savePassword">
        <ErrorBanner :error="password.error.value" />
        <UiField :label="$t('fields.currentPassword')" :error="password.fieldErr('current_password')">
          <input v-model="pw.current_password" type="password" autocomplete="current-password" required :aria-invalid="!!password.fieldErr('current_password')" />
        </UiField>
        <div class="grid-2">
          <UiField :label="$t('fields.newPassword')" :error="password.fieldErr('new_password')" :hint="$t('auth.passwordHint')">
            <input v-model="pw.new_password" type="password" autocomplete="new-password" required minlength="10" :aria-invalid="!!password.fieldErr('new_password')" />
          </UiField>
          <UiField :label="$t('fields.confirmPassword')" :error="password.fieldErr('confirm')">
            <input v-model="pw.confirm" type="password" autocomplete="new-password" required minlength="10" :aria-invalid="!!password.fieldErr('confirm')" />
          </UiField>
        </div>
        <div class="row">
          <UiButton type="submit" :loading="password.busy.value">{{ $t('settings.changePassword') }}</UiButton>
          <span v-if="pwSaved" class="small ok" role="status">{{ $t('settings.passwordChanged') }}</span>
        </div>
      </form>
    </UiCard>

    <UiCard :title="$t('settings.sessionsTitle')" :subtitle="$t('settings.sessionsSubtitle')">
      <ErrorBanner :error="sessionsForm.error.value" />
      <p v-if="sessionsForm.busy.value && !sessions.length" class="muted">{{ $t('common.loading') }}</p>
      <ul v-else class="list">
        <li v-for="s in sessions" :key="s.id">
          <div class="stack" style="gap: 0.1rem; flex: 1; min-width: 12rem">
            <strong class="ua">{{ s.user_agent || $t('settings.unknownDevice') }}</strong>
            <span class="muted small">
              {{ s.ip }} · {{ $t('settings.lastUsed', { date: formatDate(s.last_used_at) }) }}
            </span>
          </div>
          <UiBadge v-if="s.current" variant="success">{{ $t('settings.thisDevice') }}</UiBadge>
          <UiButton v-else size="sm" variant="ghost" :disabled="sessionsForm.busy.value" @click="revoke(s)">{{ $t('settings.revoke') }}</UiButton>
        </li>
      </ul>
    </UiCard>
  </div>
</template>

<style scoped>
.ok {
  color: var(--gain);
}
.ua {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
